import {
  type FormEvent,
  type ReactNode,
  useMemo,
  useRef,
  useState,
} from "react";
import { MapPin, Settings2 } from "lucide-react";

import type {
  CarrierDispatchCenter,
  CarrierDispatchCenterInput,
  CarrierServiceAreaRow,
} from "@/api/carrier";
import { AddressSearch } from "@/components/address-search";
import { DetailDrawer } from "@/components/detail-drawer";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { useUnsavedChangesGuard } from "@/hooks/use-unsaved-changes-guard";
import { centerLatLng } from "@/screens/dispatch-v2/center-location";
import { hasHttpStatus } from "@/lib/api-error";

interface FormState {
  code: string;
  name: string;
  serviceAreaId: string;
  addressLine1: string;
  addressLine2: string;
  addressCity: string;
  addressProvince: string;
  addressCountry: string;
  addressPostalCode: string;
  addressLocation: { lat: number; lng: number } | null;
  postalCodesText: string;
  timeZone: string;
  processingMinutesText: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  canHandlePickup: boolean;
  canHandleDelivery: boolean;
  canHandleReturn: boolean;
  canHandleTransfer: boolean;
  isDefault: boolean;
  isActive: boolean;
}

const EMPTY_FORM: FormState = {
  code: "",
  name: "",
  serviceAreaId: "",
  addressLine1: "",
  addressLine2: "",
  addressCity: "",
  addressProvince: "ON",
  addressCountry: "CA",
  addressPostalCode: "",
  addressLocation: null,
  postalCodesText: "",
  timeZone: "America/Toronto",
  processingMinutesText: "120",
  contactName: "",
  contactEmail: "",
  contactPhone: "",
  canHandlePickup: true,
  canHandleDelivery: true,
  canHandleReturn: false,
  canHandleTransfer: false,
  isDefault: false,
  isActive: true,
};

export function DispatchCenterFormSheet({
  center,
  serviceAreas,
  serviceAreasLoading,
  serviceAreasError,
  onClose,
  onSubmit,
}: {
  center: CarrierDispatchCenter | null;
  serviceAreas: CarrierServiceAreaRow[];
  serviceAreasLoading: boolean;
  serviceAreasError: unknown;
  onClose: () => void;
  onSubmit: (
    center: CarrierDispatchCenter | null,
    body: CarrierDispatchCenterInput
  ) => Promise<void>;
}) {
  const initialForm = useMemo(() => centerToForm(center), [center]);
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const submissionLock = useRef(false);
  const dirty = JSON.stringify(form) !== JSON.stringify(initialForm);
  const unavailableForCreate = !center && serviceAreas.length === 0;

  useUnsavedChangesGuard(
    dirty,
    "Leaving this page will discard your unsaved dispatch-center changes. Continue?"
  );

  const updateField =
    <Key extends keyof FormState>(key: Key) =>
    (value: FormState[Key]) => {
      setForm((current) => ({
        ...current,
        [key]: value,
        ...([
          "addressLine1",
          "addressCity",
          "addressProvince",
          "addressCountry",
          "addressPostalCode",
        ].includes(key) && current[key] !== value
          ? { addressLocation: null }
          : {}),
      }));
      setError(null);
    };

  const close = () => {
    if (saving) return;
    if (
      dirty &&
      !window.confirm("Discard your unsaved dispatch-center changes?")
    ) {
      return;
    }
    onClose();
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (submissionLock.current || addressLoading) return;
    const payload = buildPayload(form);
    if (payload.error) {
      setError(payload.error);
      return;
    }
    if (!payload.body) return;

    submissionLock.current = true;
    setSaving(true);
    setError(null);
    try {
      await onSubmit(center, payload.body);
    } catch (caught) {
      const response = caught as { response?: { data?: { error?: string } } };
      setError(
        hasHttpStatus(caught, 403)
          ? "You do not have permission to manage dispatch centers."
          : (response.response?.data?.error ??
              `Dispatch center could not be ${center ? "updated" : "created"}.`)
      );
    } finally {
      submissionLock.current = false;
      setSaving(false);
    }
  };

  return (
    <DetailDrawer
      open
      onOpenChange={(open) => {
        if (!open) close();
      }}
      title={center ? `Edit ${center.name}` : "New dispatch center"}
      description="Location, coverage, contacts, and operating capabilities."
      widthClassName="sm:max-w-2xl"
    >
      <form noValidate onSubmit={submit} className="space-y-5">
        {serviceAreasLoading && (
          <p role="status" className="text-sm text-muted-foreground">
            Loading service areas…
          </p>
        )}
        {!!serviceAreasError && (
          <p
            role="alert"
            className={
              serviceAreas.length > 0
                ? "text-sm text-amber-800"
                : "text-sm text-destructive"
            }
          >
            {serviceAreas.length > 0
              ? "Service areas could not be refreshed. Loaded options remain available."
              : hasHttpStatus(serviceAreasError, 403)
                ? "You do not have permission to load service areas."
                : "Service areas could not be loaded."}
          </p>
        )}
        {!center &&
          !serviceAreasLoading &&
          !serviceAreasError &&
          serviceAreas.length === 0 && (
            <p role="alert" className="text-sm text-destructive">
              Add carrier service-area coverage before creating a dispatch
              center.
            </p>
          )}
        <div className="grid gap-3 sm:grid-cols-2">
          <TextField
            id="dc-code"
            label="Code"
            value={form.code}
            disabled={saving}
            onChange={updateField("code")}
          />
          <TextField
            id="dc-name"
            label="Name"
            value={form.name}
            disabled={saving}
            onChange={updateField("name")}
          />
          <Field label="Service area" id="dc-service-area">
            <NativeSelect
              id="dc-service-area"
              value={form.serviceAreaId}
              disabled={saving || serviceAreas.length === 0}
              onChange={(event) =>
                updateField("serviceAreaId")(event.target.value)
              }
            >
              <option value="">Select service area…</option>
              {serviceAreas.map((area) => (
                <option key={area.id} value={area.id}>
                  {area.name} ({area.code})
                </option>
              ))}
            </NativeSelect>
          </Field>
          <TextField
            id="dc-time-zone"
            label="Time zone"
            value={form.timeZone}
            disabled={saving}
            onChange={updateField("timeZone")}
          />
        </div>

        <FormSection icon={<MapPin className="h-4 w-4" />} title="Address">
          <AddressSearch
            disabled={saving}
            onLoadingChange={setAddressLoading}
            onSelect={(address) => {
              setForm((current) => ({
                ...current,
                addressLine1: address.line1,
                addressLine2: address.line2,
                addressCity: address.city,
                addressProvince: address.province,
                addressCountry: address.country,
                addressPostalCode: address.postalCode,
                addressLocation: address.location,
              }));
              setError(null);
            }}
          />
          <p className="text-sm text-muted-foreground">
            Select an address to set its map location. After changing address
            fields, select it again.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <TextField
              id="dc-address-line1"
              label="Line 1"
              value={form.addressLine1}
              disabled={saving}
              onChange={updateField("addressLine1")}
            />
            <TextField
              id="dc-address-line2"
              label="Line 2"
              value={form.addressLine2}
              disabled={saving}
              onChange={updateField("addressLine2")}
            />
            <TextField
              id="dc-address-city"
              label="City"
              value={form.addressCity}
              disabled={saving}
              onChange={updateField("addressCity")}
            />
            <TextField
              id="dc-address-province"
              label="Province"
              value={form.addressProvince}
              disabled={saving}
              onChange={updateField("addressProvince")}
            />
            <TextField
              id="dc-address-country"
              label="Country"
              value={form.addressCountry}
              disabled={saving}
              onChange={updateField("addressCountry")}
            />
            <TextField
              id="dc-address-postal"
              label="Postal code"
              value={form.addressPostalCode}
              disabled={saving}
              onChange={updateField("addressPostalCode")}
            />
          </div>
        </FormSection>

        <FormSection
          icon={<Settings2 className="h-4 w-4" />}
          title="Operations and contact"
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <TextField
              id="dc-postal-codes"
              label="Covered postal prefixes"
              value={form.postalCodesText}
              disabled={saving}
              onChange={updateField("postalCodesText")}
            />
            <TextField
              id="dc-processing"
              label="Processing minutes"
              type="number"
              inputMode="numeric"
              value={form.processingMinutesText}
              disabled={saving}
              onChange={updateField("processingMinutesText")}
            />
            <TextField
              id="dc-contact-name"
              label="Contact name"
              value={form.contactName}
              disabled={saving}
              onChange={updateField("contactName")}
            />
            <TextField
              id="dc-contact-email"
              label="Contact email"
              type="email"
              inputMode="email"
              value={form.contactEmail}
              disabled={saving}
              onChange={updateField("contactEmail")}
            />
            <TextField
              id="dc-contact-phone"
              label="Contact phone"
              type="tel"
              inputMode="tel"
              value={form.contactPhone}
              disabled={saving}
              onChange={updateField("contactPhone")}
            />
          </div>
          <div className="grid gap-1 sm:grid-cols-2">
            <CheckboxField
              id="dc-can-handle-pickup"
              label="Handles pickups"
              checked={form.canHandlePickup}
              disabled={saving}
              onChange={updateField("canHandlePickup")}
            />
            <CheckboxField
              id="dc-can-handle-delivery"
              label="Handles deliveries"
              checked={form.canHandleDelivery}
              disabled={saving}
              onChange={updateField("canHandleDelivery")}
            />
            <CheckboxField
              id="dc-can-handle-return"
              label="Handles returns"
              checked={form.canHandleReturn}
              disabled={saving}
              onChange={updateField("canHandleReturn")}
            />
            <CheckboxField
              id="dc-can-handle-transfer"
              label="Handles transfers"
              checked={form.canHandleTransfer}
              disabled={saving}
              onChange={updateField("canHandleTransfer")}
            />
            <CheckboxField
              id="dc-is-default"
              label="Default center"
              checked={form.isDefault}
              disabled={saving}
              onChange={updateField("isDefault")}
            />
            <CheckboxField
              id="dc-is-active"
              label="Active"
              checked={form.isActive}
              disabled={saving}
              onChange={updateField("isActive")}
            />
          </div>
        </FormSection>

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="flex flex-col-reverse gap-2 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-xs text-muted-foreground">
            {dirty ? "Unsaved changes" : "No unsaved changes"}
          </span>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button
              type="button"
              variant="outline"
              size="touch"
              className="w-full sm:w-auto"
              disabled={saving}
              onClick={close}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="touch"
              className="w-full sm:w-auto"
              disabled={
                saving ||
                addressLoading ||
                unavailableForCreate ||
                (!dirty && !!center)
              }
            >
              {saving ? "Saving…" : center ? "Save changes" : "Create center"}
            </Button>
          </div>
        </div>
      </form>
    </DetailDrawer>
  );
}

function FormSection({
  icon,
  title,
  children,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-3 border-t pt-4">
      <h3 className="flex items-center gap-2 text-sm font-semibold">
        {icon}
        {title}
      </h3>
      {children}
    </section>
  );
}

function Field({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="text-xs font-medium">
        {label}
      </label>
      {children}
    </div>
  );
}

function TextField({
  id,
  label,
  type,
  inputMode,
  value,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  type?: string;
  inputMode?: "numeric" | "email" | "tel";
  value: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <Field id={id} label={label}>
      <Input
        id={id}
        type={type ?? "text"}
        inputMode={inputMode}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
    </Field>
  );
}

function CheckboxField({
  id,
  label,
  checked,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label
      htmlFor={id}
      className="flex min-h-11 items-center gap-3 text-sm md:min-h-0"
    >
      <Checkbox
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(value) => onChange(value === true)}
      />
      <span>{label}</span>
    </label>
  );
}

function centerToForm(center: CarrierDispatchCenter | null): FormState {
  if (!center) {
    return { ...EMPTY_FORM };
  }
  return {
    code: center.code,
    name: center.name,
    serviceAreaId: center.serviceAreaId,
    addressLine1: center.address?.line1 ?? "",
    addressLine2: center.address?.line2 ?? "",
    addressCity: center.address?.city ?? "",
    addressProvince: center.address?.province ?? "",
    addressCountry: center.address?.country ?? "CA",
    addressPostalCode: center.address?.postalCode ?? "",
    addressLocation: centerLatLng(center),
    postalCodesText: center.postalCodes.join(", "),
    timeZone: center.timeZone,
    processingMinutesText: String(center.processingMinutes),
    contactName: center.contactName ?? "",
    contactEmail: center.contactEmail ?? "",
    contactPhone: center.contactPhone ?? "",
    canHandlePickup: center.canHandlePickup,
    canHandleDelivery: center.canHandleDelivery,
    canHandleReturn: center.canHandleReturn,
    canHandleTransfer: center.canHandleTransfer,
    isDefault: center.isDefault,
    isActive: center.isActive,
  };
}

function buildPayload(form: FormState): {
  body?: CarrierDispatchCenterInput;
  error?: string;
} {
  const required = [
    form.code,
    form.name,
    form.serviceAreaId,
    form.addressLine1,
    form.addressCity,
    form.addressProvince,
    form.addressCountry,
  ];
  if (required.some((value) => !value.trim())) {
    return {
      error: "Enter a code, name, service area, and complete primary address.",
    };
  }
  if (!form.addressLocation) {
    return {
      error: "Select an address with a valid map location before saving.",
    };
  }
  const processingMinutes = Number(form.processingMinutesText);
  if (!Number.isInteger(processingMinutes) || processingMinutes < 0) {
    return { error: "Processing minutes must be a whole non-negative number." };
  }
  if (
    form.contactEmail.trim() &&
    !/^\S+@\S+\.\S+$/.test(form.contactEmail.trim())
  ) {
    return { error: "Enter a valid contact email address." };
  }
  if (form.isDefault && !form.isActive) {
    return { error: "A default dispatch center must remain active." };
  }
  return {
    body: {
      code: form.code.trim(),
      name: form.name.trim(),
      serviceAreaId: form.serviceAreaId,
      address: {
        location: form.addressLocation,
        line1: form.addressLine1.trim(),
        line2: cleanNullable(form.addressLine2),
        city: form.addressCity.trim(),
        province: form.addressProvince.trim(),
        country: form.addressCountry.trim(),
        postalCode: cleanNullable(form.addressPostalCode),
      },
      postalCodes: Array.from(
        new Set(
          form.postalCodesText
            .split(",")
            .map((part) => part.trim().toUpperCase())
            .filter(Boolean)
        )
      ),
      timeZone: form.timeZone.trim() || "America/Toronto",
      processingMinutes,
      contactName: cleanNullable(form.contactName),
      contactEmail: cleanNullable(form.contactEmail),
      contactPhone: cleanNullable(form.contactPhone),
      canHandlePickup: form.canHandlePickup,
      canHandleDelivery: form.canHandleDelivery,
      canHandleReturn: form.canHandleReturn,
      canHandleTransfer: form.canHandleTransfer,
      isDefault: form.isDefault,
      isActive: form.isActive,
    },
  };
}

function cleanNullable(value: string): string | null {
  const trimmed = value.trim();
  return trimmed || null;
}
